import React, { useState, useRef, useEffect } from 'react';
import { useEvalStore, RED_TEAM_PROMPTS } from '../../evals/evalStore.js';
import {
  runInterferenceGolden,
  runBioscanTopicGolden,
} from '../../evals/interferenceEvals.js';

/**
 * HudEvalStrip — the v3 eval telemetry strip (the .eval-strip / .es-* look from
 * DeadSignalHUD's EvalStrip), but driven by the REAL useEvalStore instead of
 * mock counters. Six metric chips with dropdown panels plus EXPORT / GOLDEN SETS
 * / RED-TEAM / LLM JUDGE action chips, all reading live aggregates.
 *
 * Drop this in as the first child of the .hud frame.
 */

const fmtTok = (n) => (n == null ? '0' : Number(n).toLocaleString());
const fmtCost = (n) => {
  if (n == null || n === 0) return '$0.000';
  if (n < 0.001) return `$${n.toFixed(5)}`;
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(3)}`;
};
const pct = (x) => (x == null ? '--' : `${Math.round(x * 100)}%`);

export default function HudEvalStrip({ onRunProbe } = {}) {
  // ── Real store wiring ──────────────────────────────────
  const records = useEvalStore((s) => s.records);
  const settings = useEvalStore((s) => s.settings);
  const setLlmJudge = useEvalStore((s) => s.setLlmJudge);
  const clear = useEvalStore((s) => s.clear);
  const exportGoldenSet = useEvalStore((s) => s.exportGoldenSet);
  const exportCsv = useEvalStore((s) => s.exportCsv);
  const aggregates = useEvalStore((s) => s.aggregates);

  const agg = aggregates();
  const turns = agg.total;
  const tokens = agg.sessionTokens || { prompt: 0, candidate: 0, total: 0, cached: 0 };
  const savings = agg.contextSavings || {};
  const dims = agg.dims || [];

  // RULES x/7: real per-dimension pass rates. A dim "passes" if its passRate is
  // 1 (no failures). Denominator is the number of evaluator dimensions that have
  // actually scored this session; until any turn is graded there are none, so we
  // fall back to a neutral 0/7 placeholder (the deterministic suite has 7 rules).
  const RULE_TOTAL = 7;
  const dimsScored = dims.length;
  const dimsPassing = dims.filter((d) => d.passRate === 1 || d.passRate === null).length;
  const rulesVal = dimsScored ? `${dimsPassing}/${dimsScored}` : `0/${RULE_TOTAL}`;

  // ── Local UI state (dropdowns / golden-set runs) ───────
  const [active, setActive] = useState(null);
  const [gate, setGate] = useState(null);
  const [topics, setTopics] = useState(null);
  const stripRef = useRef(null);

  // close dropdown on outside click
  useEffect(() => {
    if (!active) return;
    const h = (e) => {
      if (stripRef.current && !stripRef.current.contains(e.target)) setActive(null);
    };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h);
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('touchstart', h);
    };
  }, [active]);

  const tog = (id) => setActive((a) => (a === id ? null : id));

  const btn = (label, onClick, variant) => {
    const cls = 'hb' + (variant === 'primary' ? ' on' : '') + (variant === 'danger' ? ' rd' : '');
    return (
      <button key={label} className={cls} onClick={onClick}>
        {label}
      </button>
    );
  };

  // ── Metric chip definitions (REAL values) ──────────────
  const chips = [
    { id: 'turns',   val: String(turns),                            lbl: 'TURNS',   am: false },
    { id: 'rules',   val: rulesVal,                                 lbl: 'RULES',   am: false },
    { id: 'latency', val: agg.avgLatencyMs != null ? `${agg.avgLatencyMs} ms` : '—', lbl: 'AVG LAT', am: true },
    { id: 'tokens',  val: fmtTok(tokens.total),                     lbl: 'TOKENS',  am: false },
    { id: 'cost',    val: fmtCost(agg.sessionCost),                 lbl: 'COST',    am: true },
    { id: 'cache',   val: fmtCost(agg.costSavedTotal || 0),         lbl: 'CACHE',   am: true },
  ];

  const onExport = (kind) => {
    if (kind === 'csv') exportCsv();
    else exportGoldenSet();
    setActive(null);
  };

  // ── Dropdown content per chip (REAL values) ────────────
  const dropContent = () => {
    const X = (
      <button className="glass-x" style={{ fontSize: '7px' }} onClick={() => setActive(null)}>
        [×]
      </button>
    );
    switch (active) {
      case 'turns':
        return (
          <>
            <div className="drop-hdr">TURNS {X}</div>
            <div className="drop-desc">
              Total scored NPC turns this session. One record per graded model call.
            </div>
            <div className="drop-row hi"><span>total turns</span><span>{turns}</span></div>
            <div className="drop-row"><span>live coverage</span><span>{pct(agg.liveCoverage)}</span></div>
            <div className="drop-row" style={{ paddingBottom: 8 }}>
              <span>dims scored</span><span>{dimsScored}</span>
            </div>
          </>
        );

      case 'rules':
        return (
          <>
            <div className="drop-hdr">PASS RATES {X}</div>
            <div className="drop-desc">
              Deterministic evaluator pass rates this session.
              {turns === 0 ? ' No turns scored yet.' : ''}
            </div>
            {dimsScored > 0 ? (
              <div style={{ paddingBottom: 8 }}>
                {dims.map((d) => (
                  <div key={d.id} className="drop-row">
                    <span>{d.label || d.id}</span>
                    <span>{pct(d.passRate)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '4px 12px 10px', fontSize: '8px', color: 'var(--text-muted)' }}>
                no turns scored yet — run a red-team probe or transmit to Craine.
              </div>
            )}
          </>
        );

      case 'latency':
        return (
          <>
            <div className="drop-hdr">AVG LATENCY {X}</div>
            <div className="drop-desc">
              Mean model response time across turns that reported latency. Excludes UI render.
            </div>
            <div className="drop-row hi">
              <span>avg latency</span>
              <span>{agg.avgLatencyMs != null ? `${agg.avgLatencyMs} ms` : '— ms'}</span>
            </div>
            <div className="drop-row" style={{ paddingBottom: 8 }}>
              <span>turns measured</span><span>{turns}</span>
            </div>
          </>
        );

      case 'tokens':
        return (
          <>
            <div className="drop-hdr">TOTAL TOKENS {X}</div>
            <div className="drop-desc">
              Running token totals for all model calls this session. Cached input billed at the
              Flash cached rate.
            </div>
            <div className="drop-row"><span>input tokens</span><span>{fmtTok(tokens.prompt)}</span></div>
            <div className="drop-row"><span>output tokens</span><span>{fmtTok(tokens.candidate)}</span></div>
            <div className="drop-row hi"><span>total tokens</span><span>{fmtTok(tokens.total)}</span></div>
            <div className="drop-row" style={{ paddingBottom: 4 }}>
              <span>cached (input)</span><span>{fmtTok(tokens.cached)}</span>
            </div>
            <div className="drop-note">Flash: $0.15 in / $0.60 out / $0.04 cached (per 1M tokens)</div>
          </>
        );

      case 'cost':
        return (
          <>
            <div className="drop-hdr">SESSION COST {X}</div>
            <div className="drop-desc">
              Estimated cost at Flash pricing. LLM judge calls billed separately if enabled.
            </div>
            <div className="drop-row hi"><span>session cost</span><span>{fmtCost(agg.sessionCost)}</span></div>
            <div className="drop-row"><span>avg per turn</span><span>{fmtCost(agg.avgCostPerTurn)}</span></div>
            <div className="drop-row"><span>Flash — input</span><span>$0.15/1M</span></div>
            <div className="drop-row"><span>Flash — output</span><span>$0.60/1M</span></div>
            <div className="drop-row" style={{ paddingBottom: 8 }}>
              <span>Flash — cached</span><span>$0.04/1M</span>
            </div>
          </>
        );

      case 'cache':
        return (
          <>
            <div className="drop-hdr">CONTEXT MANAGEMENT {X}</div>
            <div className="drop-desc">
              Savings from prompt caching, RAG chunk compression, and conversation summarization.
            </div>
            <div className="drop-row hi"><span>cache discount</span><span>{fmtCost(savings.cacheSaved || 0)}</span></div>
            <div className="drop-row"><span>cache hit tokens</span><span>{fmtTok(tokens.cached)}</span></div>
            <div className="drop-row"><span>RAG chunks compressed</span><span>{savings.ragChunksEstablished || 0}</span></div>
            <div className="drop-row"><span>RAG tokens saved</span><span>~{fmtTok(savings.ragTokensSaved || 0)}</span></div>
            <div className="drop-row"><span>summaries fired</span><span>{savings.summaryEvents || 0}</span></div>
            <div className="drop-row"><span>summary tokens saved</span><span>~{fmtTok(savings.summaryTokensSaved || 0)}</span></div>
            <div className="drop-row hi rule"><span>total est. savings</span><span>{fmtCost(agg.costSavedTotal || 0)}</span></div>
            <div className="drop-note">= cache discount + (RAG + summary tokens saved) × Flash input rate</div>
          </>
        );

      case 'export':
        return (
          <>
            <div className="drop-hdr">EXPORT SESSION DATA {X}</div>
            <div className="drop-desc">
              Export current eval telemetry to file. Clear resets session counters without exporting.
            </div>
            <div className="drop-actions">
              {btn('EXPORT JSON', () => onExport('json'))}
              {btn('EXPORT CSV', () => onExport('csv'))}
              {btn('CLEAR SESSION', () => { clear(); setActive(null); }, 'danger')}
            </div>
          </>
        );

      case 'golden':
        return (
          <>
            <div className="drop-hdr">GOLDEN SETS (FREE) {X}</div>
            <div className="drop-desc">
              Pre-written test cases for the interference gate classifier and topic detector. Runs
              against code logic only — no model calls, always free.
            </div>
            <div className="drop-actions" style={{ paddingBottom: gate || topics ? 4 : 8 }}>
              {btn('RUN GATE TESTS', () => setGate(runInterferenceGolden()))}
              {btn('RUN TOPIC TESTS', () => setTopics(runBioscanTopicGolden()))}
            </div>
            {gate && (
              <div style={{ padding: '0 12px 6px' }}>
                <div className="gs-hdr">
                  Interference gate: {gate.passed}/{gate.total} passed
                </div>
                {gate.cases.map((c, i) => (
                  <div key={i} className="gs-row">
                    <span className="gs-chk">{c.pass ? '✓' : '✗'}</span>
                    <span className="gs-txt">{c.input}</span>
                    <span className={'gs-tag' + (c.actual ? ' blk' : '')}>
                      {c.actual ? 'blocked' : 'clean'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {topics && (
              <div style={{ padding: '0 12px 8px' }}>
                <div className="gs-hdr" style={{ marginTop: gate ? 8 : 0 }}>
                  Topic classifier: {topics.passed}/{topics.total} passed
                </div>
                {topics.cases.map((c, i) => (
                  <div key={i} className="gs-row">
                    <span className="gs-chk">{c.pass ? '✓' : '✗'}</span>
                    <span className="gs-txt">{c.input}</span>
                    <span className="gs-tag">{c.actual}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        );

      case 'redteam':
        return (
          <>
            <div className="drop-hdr">RED-TEAM PROBES {X}</div>
            <div className="drop-desc">
              Curated adversarial inputs from the eval harness. Each targets a known attack vector.
              {onRunProbe
                ? ' Click a probe to fire its payload live: it transmits through the conversation manager and is scored on the strip.'
                : ' Payloads are listed for copy-in during an active interview.'}
            </div>
            <div className="drop-probes">
              {RED_TEAM_PROMPTS.map((p) => (
                <button
                  key={p.label}
                  className="gs-row gs-probe"
                  title={p.input}
                  disabled={!onRunProbe}
                  onClick={() => {
                    if (!onRunProbe) return;
                    onRunProbe(p.input);
                    setActive(null);
                  }}
                >
                  <span className="gs-chk">▶</span>
                  <span className="gs-txt">{p.label}</span>
                </button>
              ))}
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="eval-strip" ref={stripRef}>
      <div className="es-row">

        {/* ── Metric chips (real data) ── */}
        {chips.map((c) => (
          <button
            key={c.id}
            className={'es-chip' + (c.am ? ' am' : '') + (active === c.id ? ' act' : '')}
            onClick={() => tog(c.id)}
          >
            <span className={'es-v' + (c.am ? ' am' : '')}>{c.val}</span>
            <span className="es-l">{c.lbl}</span>
          </button>
        ))}

        {/* vertical rule */}
        <div className="es-sep"></div>

        {/* ── Action chips ── */}
        <button className={'es-act' + (active === 'export' ? ' act' : '')} onClick={() => tog('export')}>EXPORT ▲</button>
        <button className={'es-act' + (active === 'golden' ? ' act' : '')} onClick={() => tog('golden')}>GOLDEN SETS ▲</button>
        <button className={'es-act' + (active === 'redteam' ? ' act' : '')} onClick={() => tog('redteam')}>RED-TEAM ▲</button>

        {/* ── LLM Judge toggle (real store setting) ── */}
        <button
          className={'es-act' + (settings.llmJudge ? ' act' : '')}
          onClick={() => setLlmJudge(!settings.llmJudge)}
        >
          {settings.llmJudge ? '✓' : '□'} LLM JUDGE
        </button>

      </div>

      {/* ── Dropdown panel ── */}
      {active && <div className="eval-drop">{dropContent()}</div>}
    </div>
  );
}
